// ABOUTME: Bottom-of-sidebar user menu - a button with the signed-in user's name and icon
// ABOUTME: Opens upward with Guide, Tutorial, API Docs, Settings and Log Out

import type { BrowserAuth } from '@boundarylab/auth/browser';
import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useSignOut } from '../hooks/useSignOut';
import { auth as defaultAuth } from '../lib/auth-client';
import { ChevronDownIcon, SettingsCogIcon, SignOutIcon, UserIcon } from './sidebar-icons';
import { isItemActive, navLinkClass } from './sidebar-link';
import { helpItems, type NavItem } from './sidebar-nav';

const menuItems: NavItem[] = [
  ...helpItems,
  { path: '/settings', label: 'Settings', icon: <SettingsCogIcon />, match: ['/settings'] },
];

interface SidebarUserMenuProps {
  isCollapsed: boolean;
  auth?: BrowserAuth;
}

export function SidebarUserMenu({ isCollapsed, auth = defaultAuth }: SidebarUserMenuProps) {
  const { pathname } = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const { claims } = useAuth(auth);
  const { signOut, signingOut } = useSignOut(auth);
  const name = claims?.email.split('@')[0];

  useEffect(() => {
    if (!isOpen) return;
    const closeOnEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [isOpen]);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-label={name ? `${name} account menu` : 'Account menu'}
        className={`flex items-center gap-3 w-full py-2 rounded-xl text-base font-medium text-white hover:bg-neutral-900 transition-colors ${
          isCollapsed ? 'justify-center px-2' : 'px-3'
        }`}
      >
        <span className="flex items-center justify-center w-8 h-8 rounded-full bg-neutral-800 text-neutral-300 flex-shrink-0">
          <UserIcon />
        </span>
        {!isCollapsed && (
          <>
            <span className="truncate flex-1 text-left">{name || 'Account'}</span>
            <ChevronDownIcon
              className={`w-4 h-4 text-neutral-500 flex-shrink-0 transition-transform ${isOpen ? '' : 'rotate-180'}`}
            />
          </>
        )}
      </button>

      {isOpen && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-50 cursor-default bg-transparent border-none"
            onClick={() => setIsOpen(false)}
            tabIndex={-1}
            aria-hidden="true"
          />
          <div
            className={`absolute bottom-full mb-2 z-50 p-2 space-y-1 bg-neutral-900 border border-neutral-800 rounded-xl shadow-lg ${
              isCollapsed ? 'left-0 w-56' : 'left-0 right-0'
            }`}
          >
            {menuItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setIsOpen(false)}
                className={navLinkClass(isItemActive(item, pathname), false)}
              >
                {item.icon}
                <span>{item.label}</span>
              </NavLink>
            ))}
            <button
              type="button"
              onClick={() => void signOut()}
              disabled={signingOut}
              className={`${navLinkClass(false, false)} w-full text-left`}
            >
              <SignOutIcon />
              <span>Log Out</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
